import {
  type DrawDefinition,
  type DrawStructure,
  type Entry,
  type SeedAssignment,
} from '@clubspark-node/clubspark-tennis-types';
import { type SeedingProfileEnum } from '@clubspark-node/clubspark-tennis-types/dist/types/enums/seeding_profile';
import { drawDefinitionConstants, factoryConstants, tournamentEngine } from 'tods-competition-factory';

import { calcEliminationDrawSize, getSeedsCount } from 'components/dialogs/draw/utils';
import { supportedDrawTypes } from 'constants/draws';
import { matchUpFormats } from 'functions/scoring/matchUpFormats';
import { type LevelConfigFormats } from 'hooks/useLevelConfiguration';
import { type MatchUpFormatInterface } from 'typedefs/scoreFormatTypes';
import { type FlightProfile, type ParticipantInterface } from 'typedefs/store/tmxTypes';
import { isPowerOf2 } from 'utils/math';

const { entryStatusConstants } = factoryConstants;
const { STRUCTURE_SELECTED_STATUSES } = entryStatusConstants;
const { VOLUNTARY_CONSOLATION, MAIN, QUALIFYING } = drawDefinitionConstants;

export interface AddDrawValues {
  advancedPerGroup: string;
  advancementType: string;
  automated: string;
  drawName: string;
  drawSize: number;
  drawStage: 'MAIN' | 'QUALIFYING';
  drawType: DrawTypesEnum;
  matchUpFormat: string;
  placeRemainingPlayers: boolean;
  playoffStructure: DrawTypesEnum;
  qualifiersCount: number;
  qualifyingDrawSize: number;
  qualifyingSeedsCount: number;
  scoring: string;
  seedingProfile: { positioning: SeedingProfileEnum; groupSeedingThreshold: number[] };
  seedsCount: number;
  structureOptions: { groupSize: string };
  tieFormatName: string;
  voluntaryConsolation: { structureName: string } | boolean;
}

export enum AdvancementTypeEnum {
  TOP_FINISHERS = 'TOP_FINISHERS',
  GROUP_POSITION = 'GROUP_POSITION',
}

export enum DrawTypesEnum {
  ROUND_ROBIN = 'ROUND_ROBIN',
  FEED_IN_CHAMPIONSHIP_TO_QF = 'FEED_IN_CHAMPIONSHIP_TO_QF',
  CURTIS_CONSOLATION = 'CURTIS_CONSOLATION',
  SINGLE_ELIMINATION = 'SINGLE_ELIMINATION',
  FEED_IN_CHAMPIONSHIP_TO_SF = 'FEED_IN_CHAMPIONSHIP_TO_SF',
  VOLUNTARY_CONSOLATION = 'VOLUNTARY_CONSOLATION',
  ROUND_ROBIN_WITH_PLAYOFF = 'ROUND_ROBIN_WITH_PLAYOFF',
  FIRST_MATCH_LOSER_CONSOLATION = 'FIRST_MATCH_LOSER_CONSOLATION',
  COMPASS = 'COMPASS',
  MODIFIED_FEED_IN_CHAMPIONSHIP = 'MODIFIED_FEED_IN_CHAMPIONSHIP',
  FEED_IN_CHAMPIONSHIP_TO_R16 = 'FEED_IN_CHAMPIONSHIP_TO_R16',
  FEED_IN_CHAMPIONSHIP = 'FEED_IN_CHAMPIONSHIP',
  AD_HOC = 'AD_HOC',
}

export type EventFormat =
  | 'RR'
  | 'FICQ'
  | 'CC'
  | 'COMP'
  | 'FIC16'
  | 'FICSF'
  | 'MFIC'
  | 'KO'
  | 'RRPO'
  | 'FMLC'
  | 'VC'
  | 'FIC'
  | 'AD_HOC';

export const mapDrawType = (drawType: DrawTypesEnum): { key: EventFormat; value: string } => {
  switch (drawType) {
    case DrawTypesEnum.ROUND_ROBIN:
      return { key: 'RR', value: 'round robin' };
    case DrawTypesEnum.FEED_IN_CHAMPIONSHIP_TO_QF:
      return { key: 'FICQ', value: 'feed in championship to qf' };
    case DrawTypesEnum.CURTIS_CONSOLATION:
      return { key: 'CC', value: 'curtis consolation' };
    case DrawTypesEnum.COMPASS:
      return { key: 'COMP', value: 'compass' };
    case DrawTypesEnum.FEED_IN_CHAMPIONSHIP_TO_R16:
      return { key: 'FIC16', value: 'feed in championship to r16' };
    case DrawTypesEnum.FEED_IN_CHAMPIONSHIP_TO_SF:
      return { key: 'FICSF', value: 'feed in championship to sf' };
    case DrawTypesEnum.MODIFIED_FEED_IN_CHAMPIONSHIP:
      return { key: 'MFIC', value: 'modified feed in championship' };
    case DrawTypesEnum.SINGLE_ELIMINATION:
      return { key: 'KO', value: 'single elimination' };
    case DrawTypesEnum.ROUND_ROBIN_WITH_PLAYOFF:
      return { key: 'RRPO', value: 'round robin with playoff' };
    case DrawTypesEnum.FIRST_MATCH_LOSER_CONSOLATION:
      return { key: 'FMLC', value: 'first match loser consolation' };
    case DrawTypesEnum.VOLUNTARY_CONSOLATION:
      return { key: 'VC', value: 'voluntary consolation' };
    case DrawTypesEnum.FEED_IN_CHAMPIONSHIP:
      return { key: 'FIC', value: 'feed in championship' };
    case DrawTypesEnum.AD_HOC:
      return { key: 'AD_HOC', value: 'ad hoc' };
  }
};
export const mapLevelEventFormatToDrawTypes = (eventFormat: EventFormat): DrawTypesEnum => {
  switch (eventFormat) {
    case 'RR':
      return DrawTypesEnum.ROUND_ROBIN;
    case 'FICQ':
      return DrawTypesEnum.FEED_IN_CHAMPIONSHIP_TO_QF;
    case 'CC':
      return DrawTypesEnum.CURTIS_CONSOLATION;
    case 'COMP':
      return DrawTypesEnum.COMPASS;
    case 'FIC16':
      return DrawTypesEnum.FEED_IN_CHAMPIONSHIP_TO_R16;
    case 'FICSF':
      return DrawTypesEnum.FEED_IN_CHAMPIONSHIP_TO_SF;
    case 'MFIC':
      return DrawTypesEnum.MODIFIED_FEED_IN_CHAMPIONSHIP;
    case 'KO':
      return DrawTypesEnum.SINGLE_ELIMINATION;
    case 'RRPO':
      return DrawTypesEnum.ROUND_ROBIN_WITH_PLAYOFF;
    case 'FMLC':
      return DrawTypesEnum.FIRST_MATCH_LOSER_CONSOLATION;
    case 'VC':
      return DrawTypesEnum.VOLUNTARY_CONSOLATION;
    case 'FIC':
      return DrawTypesEnum.FEED_IN_CHAMPIONSHIP;
    case 'AD_HOC':
      return DrawTypesEnum.AD_HOC;
    default:
      return DrawTypesEnum.SINGLE_ELIMINATION;
  }
};

export const mappedDrawTypeOptions = Object.values<string>(supportedDrawTypes)
  .filter((f) => f)
  .map((value) => ({
    key: value.toUpperCase() as DrawTypesEnum,
    value: value.toLowerCase().replace(/_/g, ' '),
  }));

export const getEventEnteredParticipantIds = (eventEntries: Entry[]) =>
  (eventEntries || [])
    .filter(({ entryStatus }) => STRUCTURE_SELECTED_STATUSES.includes(entryStatus))
    .map(({ participantId }) => participantId);

export const getSeedAssignments = (flight: FlightProfile, drawDefinitions: DrawDefinition[]) => {
  const structure =
    flight &&
    drawDefinitions
      ?.find((definition) => definition.drawId === flight.drawId)
      ?.structures?.find((structure) => structure.structureName === MAIN);
  return (
    structure && (tournamentEngine.getStructureSeedAssignments({ structure })?.seedAssignments as SeedAssignment[])
  );
};

export const getEntryStageParticipants = (
  entryStage: 'MAIN' | 'QUALIFYING',
  eventParticipants: ParticipantInterface[],
  eventId: string,
) =>
  eventParticipants.filter((p) => {
    const participantEntryStage = p?.events?.find((e) => e?.eventId === eventId)?.entryStage;
    return participantEntryStage === entryStage || participantEntryStage === undefined;
  });

export const getDrawSeedingCount = (flight: FlightProfile, drawDefinitions: DrawDefinition[]) =>
  flight &&
  drawDefinitions
    ?.find((definition) => definition.drawId === flight.drawId)
    ?.structures?.find((structure) => structure.structureName === MAIN)
    ?.seedAssignments?.filter((s) => s?.participantId).length;

export const getMaxSeedsCount = ({
  currentParticipantsCount,
  drawSeedingCount,
  eventEnteredParticipantIds,
  eventEntries,
  flight,
  seeding,
  seedValuesCount,
}: {
  currentParticipantsCount: number;
  drawSeedingCount: number;
  eventEnteredParticipantIds: string[];
  eventEntries: Entry[];
  flight: FlightProfile;
  seeding: SeedAssignment[];
  seedValuesCount: number;
}) => {
  const entries = flight ? flight?.drawEntries : eventEntries || [];
  const participantCount = flight ? currentParticipantsCount : eventEnteredParticipantIds.length;
  return !drawSeedingCount
    ? seedValuesCount
    : getSeedsCount(calcEliminationDrawSize(participantCount), entries, { seeding });
};

export const getParticipantsCount = (entries: Entry[], structure: DrawStructure, isAddQualifyingDrawToMain: boolean) =>
  entries.filter((entry) => {
    if (structure?.stage === VOLUNTARY_CONSOLATION) return entry.entryStage === VOLUNTARY_CONSOLATION;
    if (isAddQualifyingDrawToMain) return entry.entryStage === QUALIFYING;
    return STRUCTURE_SELECTED_STATUSES.includes(entry.entryStatus) && entry.entryStage !== QUALIFYING;
  })?.length ?? 0;

export const getQualifyingParticipantCount = (entries: Entry[]) =>
  entries.filter((entry) => STRUCTURE_SELECTED_STATUSES.includes(entry.entryStatus) && entry.entryStage === QUALIFYING)
    ?.length ?? 0;

export const getDefaultQualifyingSpotsCount = ({
  isAddMainDrawToQualifying,
  isAddQualifyingDrawToMain,
  drawDefinitions,
}: {
  isAddMainDrawToQualifying: boolean;
  isAddQualifyingDrawToMain: boolean;
  drawDefinitions: DrawDefinition[];
}): number =>
  ((isAddMainDrawToQualifying || isAddQualifyingDrawToMain) &&
    drawDefinitions?.[0]?.extensions?.find((e) => e?.name === 'entryProfile')?.value?.MAIN?.qualifiersCount) ||
  0;

export const getValidScoreFormats = (
  levelConfigFormats: LevelConfigFormats,
  scoringPolicy: {
    matchUpFormats?: MatchUpFormatInterface[];
    defaultMatchUpFormat?: string;
  },
) =>
  levelConfigFormats.scoreFormats?.length > 0
    ? matchUpFormats(scoringPolicy)?.formats?.filter((f) => levelConfigFormats.scoreFormats.includes(f.matchUpFormat))
    : matchUpFormats(scoringPolicy)?.formats;

export const getValidDrawTypeOptions = ({
  flight,
  splitDrawEventFormats,
  isTeam,
  level,
  isLeague = false,
}: {
  flight: FlightProfile;
  splitDrawEventFormats: EventFormat[];
  isTeam: boolean;
  level: LevelConfigFormats;
  isLeague?: boolean;
}) => {
  if (isLeague) return mappedDrawTypeOptions.filter((type) => type.key === DrawTypesEnum.AD_HOC);
  if (flight && !flight.manuallyAdded) {
    return mappedDrawTypeOptions?.filter((keyValuePair) => {
      const drawType = keyValuePair?.key;
      const mappedOption = mapDrawType(drawType);
      return (splitDrawEventFormats || []).includes(mappedOption.key);
    });
  } else {
    if (!isTeam && level?.eventFormats?.length)
      return mappedDrawTypeOptions?.filter((keyValuePair) => {
        const drawType = keyValuePair?.key;
        const mappedOption = mapDrawType(drawType);
        return level.eventFormats?.includes(mappedOption.key);
      });
    return mappedDrawTypeOptions;
  }
};

export const getFilteredDrawTypeOptions = (
  structure: DrawStructure,
  flight: FlightProfile,
  validDrawTypeOptions: {
    key: DrawTypesEnum;
    value: string;
  }[],
) =>
  structure?.stage !== String(VOLUNTARY_CONSOLATION) || !flight?.drawDefinition
    ? validDrawTypeOptions
    : validDrawTypeOptions.filter((option) => option.key === DrawTypesEnum.SINGLE_ELIMINATION);

export const getIsRoundRobin = (drawType: DrawTypesEnum) =>
  [supportedDrawTypes.ROUND_ROBIN, supportedDrawTypes.ROUND_ROBIN_WITH_PLAYOFF].filter((f) => f).includes(drawType);

export const getDefaultDrawSize = ({
  defaultDrawType,
  defaultQualifyingSpotsCount,
  isAddQualifyingDrawToMain,
  participantsCount,
}: {
  defaultDrawType: DrawTypesEnum;
  defaultQualifyingSpotsCount: number;
  isAddQualifyingDrawToMain: boolean;
  participantsCount: number;
}) => {
  const participantCount = !isAddQualifyingDrawToMain
    ? participantsCount + defaultQualifyingSpotsCount
    : participantsCount;
  return getIsRoundRobin(defaultDrawType)
    ? participantsCount + defaultQualifyingSpotsCount
    : calcEliminationDrawSize(participantCount);
};

export const getSeedingThreseholds = (participantsCount: number, draw: Partial<AddDrawValues>) => {
  const roundRobinGroupsCount = Math.ceil(participantsCount / parseInt(draw?.structureOptions?.groupSize));
  return tournamentEngine.getSeedingThresholds({
    ...(getIsRoundRobin(draw?.drawType) && { roundRobinGroupsCount }),
    participantsCount,
  }).seedingThresholds as number[];
};

export const getDrawType = (
  selectedDrawType: DrawTypesEnum,
  validDrawTypeOptions: {
    key: DrawTypesEnum;
    value: string;
  }[],
  handleOnTypeChange?: (drawType: DrawTypesEnum) => void,
) => {
  const validDrawTypes = validDrawTypeOptions?.map((keyValuePair) => keyValuePair.key);

  if (validDrawTypes.includes(selectedDrawType)) {
    return selectedDrawType;
  }

  handleOnTypeChange?.(validDrawTypes[0]);
  return validDrawTypes[0];
};

export const getSeedsCountValue = (
  draw: Partial<AddDrawValues>,
  qualifyingSeedsCount: number,
  maxSeedsCount: number,
) => {
  const maximumSeedsCount = draw.drawStage === QUALIFYING ? qualifyingSeedsCount : maxSeedsCount;
  return !getIsRoundRobin(draw.drawType) && draw.seedsCount > maximumSeedsCount ? maximumSeedsCount : draw.seedsCount;
};

export const getQualifiersCountValue = (entries: Entry[], draw: Partial<AddDrawValues>) => {
  const qualifyingParticipantsCount = entries.filter((entry) => entry.entryStage === QUALIFYING)?.length;
  return !isPowerOf2(draw.qualifiersCount) || draw.qualifiersCount > qualifyingParticipantsCount
    ? 0
    : draw.qualifiersCount;
};
